// src/components/HomeNoticias/HomeNoticias.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Slider from 'react-slick';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styles from './HomeNoticias.module.scss';
import HomeNoticiasCard from './HomeNoticiasCard';

const MotionLink = motion(Link);

const buttonHover = {
  scale: 1.1,
  y: -3,
  boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.2)',
  backgroundColor: 'var(--secondary-hover)',
  color: 'var(--text-color)',
};

const buttonTransition = {
  type: 'tween',
  ease: 'easeOut',
  duration: 0.2,
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.2 } },
};
const itemVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 50, damping: 12 },
  },
};

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

const HomeNoticias = () => {
  const [noticias, setNoticias] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const width = useWindowWidth();
  const isMobile = width < 640;

  useEffect(() => {
    const fetchNoticias = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/noticias?populate=previsualizacion&sort=publishedAt:desc&pagination[limit]=3`
        );
        const { data } = await res.json();
        const arr = (data || []).map((n) => ({
          id: n.id,
          titulo: n.titulo || 'Sin título',
          contenido: n.contenido || 'Sin contenido',
          previsualizacion: n.previsualizacion?.[0]?.url || '',
          fechaPublicacion: n.publishedAt || 'Sin fecha',
          slug: n.slug || '',
        }));
        setNoticias(arr);
      } catch (err) {
        console.error('Error fetching noticias:', err);
      }
    };
    fetchNoticias();
  }, []);

  if (noticias.length === 0) return null;

  const settings = {
    dots: true,
    infinite: true,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  return (
    <div className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.title}>Noticias</h2>

        {isMobile ? (
          <div className={styles.mobileGrid}>
            <Slider {...settings}>
              {noticias.map((n) => (
                <div key={n.id} className={styles.mobileCard}>
                  <HomeNoticiasCard noticia={n} />
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <motion.div
            className={styles.grid}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            {noticias.map((n) => (
              <motion.div
                key={n.id}
                variants={itemVariants}
                className={`
                  ${styles.cardWrapper}
                  ${
                    hoveredId === n.id
                      ? styles.grow
                      : hoveredId && hoveredId !== n.id
                      ? styles.blur
                      : ''
                  }
                `}
                onMouseEnter={() => setHoveredId(n.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <HomeNoticiasCard noticia={n} />
              </motion.div>
            ))}
          </motion.div>
        )}

        <div className={styles.verMasWrapper}>
          <MotionLink
            to="/noticias"
            className={styles.verMasButton}
            whileHover={buttonHover}
            transition={buttonTransition}
          >
            VER MÁS NOTICIAS
          </MotionLink>
        </div>
      </div>
    </div>
  );
};

export default HomeNoticias;
